import * as React from 'react';
import {
  Field,
  Input,
  InfoLabel,
  Link,
  Tag,
  TagGroup,
  makeStyles,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  inputRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '8px',
    flexWrap: 'wrap',
    width: "100%"
  },
  tagList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    flexDirection: 'row'
  },
  tagContent: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '12px',
    lineHeight: '16px',
  },
});

export type EnvironmentEntry = {
  name: string;
  identifier: string;
  type: string;
};

type MultiEnvironmentInputProps = {
  value: EnvironmentEntry[];
  onChange: (newEntries: EnvironmentEntry[]) => void;
  maxEntries?: number;
  label?: string;
  required?: boolean;
  type: string;
};

export const MultiEnvironmentInput = ({
  value,
  onChange,
  type,
  maxEntries = 10,
  label = 'Organization IDs',
  required = false,
}: MultiEnvironmentInputProps) => {
  const [guidInput, setGuidInput] = React.useState('');
  const [labelInput, setLabelInput] = React.useState('');
  const styles = useStyles();

  const isValidGuid = (guid: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(guid);

  const handleAdd = () => {
    const trimmedGuid = guidInput.trim();
    const trimmedLabel = labelInput.trim();

    if (
      trimmedGuid &&
      trimmedLabel &&
      isValidGuid(trimmedGuid) &&
      trimmedLabel.length <= 200 &&
      value.length < maxEntries &&
      !value.some(e => e.identifier === trimmedGuid)
    ) {
      onChange([...value, { identifier: trimmedGuid, name: trimmedLabel, type: type }]);
      setGuidInput('');
      setLabelInput('');
    }
  };

  const handleRemove = (guidToRemove: string) => {
    onChange(value.filter(e => e.identifier !== guidToRemove));
  };

  return (
    <Field
      required={required}
      label={
        <InfoLabel
          info={
            <>
              Each of the maximum 10 entries requires a label (max 200 characters) and a valid Organization ID.
              For help with finding Organization IDs, see{' '}
              <Link
                href="https://learn.microsoft.com/en-us/power-platform/admin/determine-org-id-name"
                target="_blank"
              >
                this guide
              </Link>. Format without brackets, e.g. "aab856fc-aa99-ba1b-ba77-6e4480c70071".
            </>
          }
        >
          {label}
        </InfoLabel>
      }
      hint={`${value.length} / ${maxEntries}`}
    >
      <div className={styles.inputRow}>
        <Input
          placeholder="Label (max 200 chars)"
          value={labelInput}
          maxLength={200}
          onChange={(_, data) => setLabelInput(data.value)}
          onBlur={() => handleAdd()}
          style={{ flex: 1 }}
        />
        <Input
          placeholder="Enter Guid and press enter to add"
          value={guidInput}
          maxLength={36}
          onChange={(_, data) => setGuidInput(data.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          onBlur={() => handleAdd()}
          style={{ flex: 1 }}
        />
      </div>

      {(guidInput && !isValidGuid(guidInput)) && (
        <div style={{ color: 'red', fontSize: 12, marginBottom: 4 }}>Invalid GUID format</div>
      )}

      {(guidInput && value.some(e => e.identifier === guidInput.trim())) && (
        <div style={{ color: 'red', fontSize: 12, marginBottom: 4 }}>GUID already in list</div>
      )}      

      <TagGroup className={styles.tagList} onDismiss={(_, data) => handleRemove(data.value)}>
        {value.map(({ name, identifier }) => (
          <Tag selected appearance='brand' key={identifier} value={identifier} secondaryText={identifier} dismissible>
              {name}
          </Tag>
        ))}
      </TagGroup>
    </Field>
  );
};
